import { checkIdentityLogin } from '@/modules/oidc';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import i18n from 'i18next';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { SEARCH_VIEW_TYPES, USER_PREFERENCES } from '../../contants/common';
import { getUserLocalStorage, setUserLocalStorage } from '../../utils/auth';
import { convertThemeApiToLocal, getErrorMessageFromAPI } from '../../utilz';
import { setLDClaimViewTypeInitial } from '../Claims/LossAndDamage/LossAndDamageClaimsActions';
import LossAndDamageClaimsTypes from '../Claims/LossAndDamage/LossAndDamageClaimsTypes';
import { setOCClaimViewTypeInitial } from '../Claims/OverCharge/OverChargeActions';
import OverChargeClaimsTypes from '../Claims/OverCharge/OverChargeTypes';
import { setAppLoader } from '../common/CommonActions';
import { setSearchViewTypeTypeInitial, setShipmentProductViewTypeInitial } from '../shipment/ShipmentActions';
import ShipmentTypes from '../shipment/ShipmentTypes';
import {
  approveRejectShipmentInFail,
  approveRejectShipmentInSuccess,
  changeCompanyInFail,
  changeCompanyInSuccess,
  forgotPasswordInError,
  forgotPasswordInSuccess,
  getUserMenuInError,
  getUserMenuInSuccess,
  getUserMenuItemsInError,
  getUserMenuItemsInSuccess,
  getUserPreferenceInError,
  getUserPreferenceInSuccess,
  getUserProfileInError,
  getUserProfileInSuccess,
  setLoginError,
  setLoginSuccess,
  updateUserPasswordInError,
  updateUserPasswordInSuccess,
  updateUserPreferenceInError,
  updateUserPreferenceInSuccess,
  updateUserProfileImageInError,
  updateUserProfileImageInSuccess,
  userProfileUpdateInError,
  userProfileUpdateInSuccess,
  userRegistrationInError,
  userRegistrationInSuccess,
} from './AuthActions';
import {
  approveRejectShipmentEffects,
  changeCompanyEffects,
  forgotPasswordEffect,
  getUserMenuEffect,
  getUserMenuItemsEffect,
  getUserPreferenceEffect,
  getUserProfileEffect,
  loginEffect,
  updateUserPasswordEffect,
  updateUserPreferenceEffect,
  updateUserProfileEffect,
  userRegistrationEffect,
} from './AuthEffects';
import AuthTypes from './AuthTypes';

function* checkLogin({ payload: { callback, ...payload } }) {
  try {
    const { data } = yield call(loginEffect, payload);
    data.userGuid = uuidv4();
    if (data?.twoFactorEnabled === false) {
      yield call(setUserLocalStorage, data);
      yield put(setAppLoader(true));
    }
    yield put(setLoginSuccess({ data: data }));
    if (callback) {
      callback(data);
    }
  } catch (e) {
    yield put(setLoginError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* getUserMenu() {
  try {
    const { data } = yield call(getUserMenuEffect);
    yield put(getUserMenuInSuccess({ data: data }));
  } catch (e) {
    yield put(getUserMenuInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* getUserMenuItems({ payload }) {
  try {
    const { data } = yield call(getUserMenuItemsEffect, payload.NavigationID);
    yield put(getUserMenuItemsInSuccess({ data: data }));
  } catch (e) {
    yield put(getUserMenuItemsInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* forgotPassword({ payload }) {
  try {
    const { data } = yield call(forgotPasswordEffect, payload);
    yield put(forgotPasswordInSuccess({ data: data }));
  } catch (e) {
    yield put(forgotPasswordInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* userRegistration({ payload }) {
  try {
    const { data } = yield call(userRegistrationEffect, payload);
    yield put(userRegistrationInSuccess({ data: data }));
  } catch (e) {
    yield put(userRegistrationInError({ message: getErrorMessageFromAPI(e) }));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: getErrorMessageFromAPI(e),
    });
  }
}

function* getUserProfile() {
  try {
    yield put(setAppLoader(true));
    const { data } = yield call(getUserProfileEffect);
    yield put(getUserProfileInSuccess({ data }));
    yield put(setAppLoader(false));
  } catch (e) {
    yield put(getUserProfileInError({ message: getErrorMessageFromAPI(e) }));
    yield put(setAppLoader(false));
  }
}

function* updateUserProfile({ payload }) {
  try {
    const { data, status } = yield call(updateUserProfileEffect, payload);
    if (status === 200) {
      yield call(showNotification, {
        type: NOTIFICATION_TYPES.SUCCESS,
        message: i18n.t('translation:default.notification.userProfileUpdated'),
      });
      yield put(userProfileUpdateInSuccess({ data }));
      yield put(setAppLoader(false));
    } else {
      yield put(setAppLoader(false));
      yield put(userProfileUpdateInError({ message: getErrorMessageFromAPI(data) }));
    }
  } catch (e) {
    yield put(userProfileUpdateInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* updateUserPassword({ payload }) {
  try {
    const { data, status } = yield call(updateUserPasswordEffect, payload);
    if (status === 200) {
      yield call(showNotification, {
        type: NOTIFICATION_TYPES.SUCCESS,
        message: i18n.t('translation:default.notification.passwordUpdated'),
      });
      yield put(updateUserPasswordInSuccess({ data: data }));
      yield put(setAppLoader(false));
    } else {
      yield put(setAppLoader(false));
      yield put(
        updateUserPasswordInError({
          message: getErrorMessageFromAPI(data, i18n.t('translation:default.notification.passwordUpdatedFailed')),
        }),
      );
    }
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(e, i18n.t('translation:default.notification.passwordUpdatedFailed'));
    yield put(
      updateUserPasswordInError({
        message: errorMessage,
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: errorMessage,
    });
  }
}

function* updateUserPreference({ payload }) {
  const userPReference = yield select((state) => state?.auth?.userPreference?.data);
  try {
    yield put(setAppLoader(true));
    const { data, status } = yield call(updateUserPreferenceEffect, payload);
    if (status === 200) {
      yield call(showNotification, {
        type: NOTIFICATION_TYPES.SUCCESS,
        message: i18n.t('translation:default.notification.preferenceUpdated'),
      });
      yield put(updateUserPreferenceInSuccess({ data: data }));
      yield put(
        getUserPreferenceInSuccess({
          data: {
            ...(userPReference ? userPReference : {}),
            ...convertThemeApiToLocal(payload),
          },
        }),
      );
      yield put(setAppLoader(false));
    } else {
      yield put(setAppLoader(false));
      yield put(
        updateUserPreferenceInError({
          message: getErrorMessageFromAPI(data, i18n.t('translation:default.notification.preferenceUpdatedFailed')),
        }),
      );
    }
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(e, i18n.t('translation:default.notification.preferenceUpdatedFailed'));
    yield put(
      updateUserPreferenceInError({
        message: errorMessage,
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: errorMessage,
    });
  }
}

function* getUserPreference({ payload }) {
  try {
    const { data } = yield call(getUserPreferenceEffect, payload);
    yield put(getUserPreferenceInSuccess({ data: convertThemeApiToLocal(data) }));
    yield put(
      setSearchViewTypeTypeInitial(
        data[USER_PREFERENCES.SHIPMENT_VIEW_TYPE] ? data[USER_PREFERENCES.SHIPMENT_VIEW_TYPE] : SEARCH_VIEW_TYPES.CARD,
      ),
    );
    yield put(
      setShipmentProductViewTypeInitial(
        data[USER_PREFERENCES.SHIPMENT_PRODUCT_VIEW_TYPE]
          ? data[USER_PREFERENCES.SHIPMENT_PRODUCT_VIEW_TYPE]
          : SEARCH_VIEW_TYPES.CARD,
      ),
    );
    yield put(
      setOCClaimViewTypeInitial(
        data[USER_PREFERENCES.OC_CLAIM_VIEW_TYPE] ? data[USER_PREFERENCES.OC_CLAIM_VIEW_TYPE] : SEARCH_VIEW_TYPES.CARD,
      ),
    );
    yield put(
      setLDClaimViewTypeInitial(
        data[USER_PREFERENCES.LD_CLAIM_VIEW_TYPE] ? data[USER_PREFERENCES.LD_CLAIM_VIEW_TYPE] : SEARCH_VIEW_TYPES.CARD,
      ),
    );
    yield put(setAppLoader(false));
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(e, i18n.t('translation:default.notification.userPreferenceFailed'));
    yield put(
      getUserPreferenceInError({
        message: errorMessage,
      }),
    );
  }
}
function* changeCompanyAction({ payload }) {
  const userData = yield select((state) => state.auth.user.data);
  try {
    const { isIdentityLogin } = checkIdentityLogin();
    if (isIdentityLogin) {
      const { userInfo } = getUserLocalStorage();
      const data = {
        ...userInfo,
        userGuid: uuidv4(),
        selectedCompany: payload.CompanyID,
      };

      yield call(setUserLocalStorage, data);
      yield put(setLoginSuccess({ data: { ...userData, selectedCompany: payload.CompanyID } }));
      yield put(changeCompanyInSuccess({ CompanyID: payload.CompanyID }));
    } else {
      const { data } = yield call(changeCompanyEffects, payload);
      data.userGuid = uuidv4();
      if (data?.twoFactorEnabled === false) {
        yield call(setUserLocalStorage, data);
      }
      yield put(setLoginSuccess({ data: data }));
      yield put(changeCompanyInSuccess({ data: data }));
    }
  } catch (e) {
    yield put(changeCompanyInFail({ message: getErrorMessageFromAPI(e) }));
  }
}

function* approveRejectShipment({ payload }) {
  try {
    const { data } = yield call(approveRejectShipmentEffects, payload);
    yield put(approveRejectShipmentInSuccess({ message: data }));
  } catch (e) {
    yield put(
      approveRejectShipmentInFail({
        message: getErrorMessageFromAPI(e, i18n.t('translation:default.notification.shipmentStatusFailed')),
      }),
    );
  }
}

function* updateUserProfileImage({ payload }) {
  try {
    const { data, status } = yield call(updateUserProfileEffect, payload);
    if (status === 200) {
      yield call(showNotification, {
        type: NOTIFICATION_TYPES.SUCCESS,
        message: i18n.t('translation:default.notification.userProfileImage'),
      });
      yield put(updateUserProfileImageInSuccess(payload));
    } else {
      yield put(updateUserProfileImageInError({ message: getErrorMessageFromAPI(data) }));
    }
  } catch (e) {
    yield put(updateUserProfileImageInError({ message: getErrorMessageFromAPI(e) }));
  }
}

function* updateShipmentGridViewType({ payload }) {
  yield delay(5000);
  const userPreference = yield select((state) => state.auth.getUserPreference.data);
  try {
    const payloadData = {
      ...userPreference,
      [USER_PREFERENCES.SHIPMENT_VIEW_TYPE]: payload,
    };
    yield call(updateUserPreferenceEffect, payloadData);
  } catch (e) {
    console.log(e);
  }
}

function* updateShipmentProductGridViewType({ payload }) {
  yield delay(5000);
  const userPreference = yield select((state) => state.auth.getUserPreference.data);
  try {
    const payloadData = {
      ...userPreference,
      [USER_PREFERENCES.SHIPMENT_PRODUCT_VIEW_TYPE]: payload,
    };
    yield call(updateUserPreferenceEffect, payloadData);
  } catch (e) {
    console.log(e);
  }
}

function* updateLDClaimGridViewType({ payload }) {
  const userPreference = yield select((state) => state.auth.getUserPreference.data);
  try {
    yield delay(5000);
    const payloadData = {
      ...userPreference,
      [USER_PREFERENCES.LD_CLAIM_VIEW_TYPE]: payload,
    };
    yield call(updateUserPreferenceEffect, payloadData);
  } catch (e) {
    console.log(e);
  }
}

function* updateOCClaimGridViewType({ payload }) {
  const userPreference = yield select((state) => state.auth.getUserPreference.data);
  try {
    yield delay(5000);
    const payloadData = {
      ...userPreference,
      [USER_PREFERENCES.OC_CLAIM_VIEW_TYPE]: payload,
    };
    yield call(updateUserPreferenceEffect, payloadData);
  } catch (e) {
    console.log(e);
  }
}

export default function* authSaga() {
  yield takeLatest(AuthTypes.LOGIN_IN_PROGRESS, checkLogin);
  yield takeLatest(AuthTypes.GET_USER_MENU_PROGRESS, getUserMenu);
  yield takeLatest(AuthTypes.GET_USER_MENU_ITEMS_PROGRESS, getUserMenuItems);
  yield takeLatest(AuthTypes.FORGOT_PASSWORD_PROGRESS, forgotPassword);
  yield takeLatest(AuthTypes.USER_REGISTRATION_PROGRESS, userRegistration);
  yield takeLatest(AuthTypes.GET_USER_PROFILE_PROGRESS, getUserProfile);
  yield takeLatest(AuthTypes.USER_PROFILE_UPDATE_PROGRESS, updateUserProfile);
  yield takeLatest(AuthTypes.UPDATE_USER_PASSWORD_PROGRESS, updateUserPassword);
  yield takeLatest(AuthTypes.UPDATE_USER_PREFERENCE_PROGRESS, updateUserPreference);
  yield takeLatest(AuthTypes.GET_USER_PREFERENCE_PROGRESS, getUserPreference);
  yield takeLatest(AuthTypes.CHANGE_COMPANY_IN_PROGRESS, changeCompanyAction);
  yield takeLatest(AuthTypes.APPROVE_REJECT_SHIPMENT_IN_PROGRESS, approveRejectShipment);
  yield takeLatest(AuthTypes.UPDATE_USER_PROFILE_IMAGE_PROGRESS, updateUserProfileImage);
  yield takeLatest(ShipmentTypes.SET_SHIPMENT_SEARCH_VIEW_TYPE, updateShipmentGridViewType);
  yield takeLatest(LossAndDamageClaimsTypes.SET_CLAIM_SEARCH_VIEW_TYPE, updateLDClaimGridViewType);
  yield takeLatest(ShipmentTypes.SET_SHIPMENT_PRODUCT_VIEW_TYPE, updateShipmentProductGridViewType);
  yield takeLatest(OverChargeClaimsTypes.SET_CLAIM_SEARCH_VIEW_TYPE, updateOCClaimGridViewType);
}
